import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";

import { menuActions } from "../store/menuSlice";
import { authActions } from "../store/authSlice";

import { MenuItem } from "./menu/MenuItem";
import { useGetUnansweredComplaintsCountQuery } from "../store/services/complaintsServices";

import { LuBell, LuActivity, LuFileText, LuFileCheck, LuFileMinus, LuFileWarning, LuStore, LuList, LuMessageSquare, LuFileSearch, LuDices, LuUsers, LuContact, LuShieldCheck, LuLandmark } from "react-icons/lu";
import {
  FiMenu,
  FiX,
  FiChevronUp,
  FiUser,
  FiLogOut,
} from "react-icons/fi";

import { isAdmin, getRoleLabel } from "../utils/roleUtils";

const MENU_GROUPS = [
  {
    label: "Menaxhimi",
    items: [
      { category: "atk_users", title: "Përdoruesit e ATK-së", icon: <LuShieldCheck size={20} />, linkTo: "/atk_users" },
      { category: "users", title: "Qytetarët", icon: <LuContact size={20} />, linkTo: "/users" },
      { category: "businesses", title: "Bizneset", icon: <LuStore size={20} />, linkTo: "/businesses" },
    ],
  },
  {
    label: "Kuponat",
    items: [
      { category: "pending_receipts", title: "Kuponat në pritje", icon: <LuFileText size={20} />, linkTo: "/pending_receipts" },
      { category: "approved_receipts", title: "Kuponat e aprovuar", icon: <LuFileCheck size={20} />, linkTo: "/approved_receipts" },
      { category: "non_fiscal_receipts", title: "Kuponat jo fiskalë", icon: <LuFileMinus size={20} />, linkTo: "/non_fiscal_receipts" },
      { category: "flagged_receipts", title: "Kuponat e parregullt", icon: <LuFileWarning size={20} />, linkTo: "/flagged_receipts" },
    ],
  },
  {
    label: "Lojërat",
    items: [
      { category: "games", title: "Lojërat", icon: <LuList size={20} />, linkTo: "/games" },
      { category: "prizes", title: "Lotaria", icon: <LuDices size={20} />, linkTo: "/prizes" },
      { category: "winners", title: "Fituesit", icon: <LuUsers size={20} />, linkTo: "/winners" },
    ],
  },
  {
    label: "Komunikimi",
    items: [
      { category: "notifications", title: "Njoftimet", icon: <LuBell size={20} />, linkTo: "/notifications" },
      { category: "complaints", title: "Ankesat", icon: <LuMessageSquare size={20} />, linkTo: "/complaints" },
    ],
  },
  {
    label: "Sistemi",
    items: [
      { category: "reports", title: "Raportet", icon: <LuFileSearch size={20} />, linkTo: "/reports" },
      ...(isAdmin()
        ? [
            { category: "banks", title: "Bankat", icon: <LuLandmark size={20} />, linkTo: "/banks" },
            { category: "audit_logs", title: "Historiku", icon: <LuActivity size={20} />, linkTo: "/audit_logs" },
          ]
        : []),
    ],
  },
];

/* ----------------------------- Profile menu ----------------------------- */

function Avatar({ initials, size = "md" }) {
  const sizes = {
    sm: "w-8 h-8 text-[11px] rounded-lg",
    md: "w-9 h-9 text-xs rounded-xl",
    lg: "w-11 h-11 text-sm rounded-xl",
  };
  return (
    <span
      className={`${sizes[size]} shrink-0 inline-flex items-center justify-center font-bold text-white bg-[var(--secondary-color)] shadow-sm`}
    >
      {initials}
    </span>
  );
}

/* Trigger shown at the bottom of the sidebar / drawer */
function ProfileTrigger({ user, fullName, initials, isOpen, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left transition ${
        isOpen ? "bg-gray-100" : "hover:bg-gray-50"
      }`}
      aria-haspopup="menu"
      aria-expanded={isOpen}
    >
      <Avatar initials={initials} />
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-gray-900 truncate tracking-tight">
          {fullName || "Përdorues"}
        </span>
        <span className="block text-[11px] text-gray-500 truncate">{getRoleLabel(user.role)}</span>
      </span>
      <FiChevronUp
        size={16}
        className={`shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? "" : "rotate-180"}`}
      />
    </button>
  );
}

function ProfileMenu({ user, fullName, initials, isOpen, onClose, onProfile, onLogout }) {
  return (
    <div
      role="menu"
      aria-hidden={!isOpen}
      className={`absolute left-3 right-3 bottom-full mb-2 z-20 rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-900/10 overflow-hidden transition duration-150 ease-out origin-bottom ${
        isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-1 scale-[0.98] pointer-events-none"
      }`}
    >
      <div className="flex items-center gap-3 p-3.5 bg-gray-50/70 border-b border-gray-100">
        <Avatar initials={initials} size="lg" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">{fullName || "Përdorues"}</div>
          {user.email && <div className="text-xs text-gray-500 truncate">{user.email}</div>}
          <span className="mt-1 inline-flex items-center rounded-md bg-[var(--secondary-color)]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--secondary-color)]">
            {getRoleLabel(user.role)}
          </span>
        </div>
      </div>

      <div className="p-1.5">
        <button
          type="button"
          role="menuitem"
          tabIndex={isOpen ? 0 : -1}
          onClick={() => {
            onClose();
            onProfile();
          }}
          className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition"
        >
          <FiUser size={16} className="text-gray-500" />
          Profili im
        </button>
      </div>

      <div className="p-1.5 border-t border-gray-100">
        <button
          type="button"
          role="menuitem"
          tabIndex={isOpen ? 0 : -1}
          onClick={onLogout}
          className="w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition"
        >
          <FiLogOut size={16} />
          Shkyçu
        </button>
      </div>
    </div>
  );
}

/* --------------------------------- Menu --------------------------------- */

export default function Menu() {
  const activeCategory = useSelector((state) => state.menu.activeCategory);
  /* Refreshed every minute so new complaints show up without reloading */
  const { data: unansweredComplaints = 0 } = useGetUnansweredComplaintsCountQuery(undefined, { pollingInterval: 60000 });
  const token = localStorage.getItem("giz_superaccess");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = jwtDecode(token);

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const mobileProfileRef = useRef(null);

  /* Body scroll lock while drawer open */
  useEffect(() => {
    if (!isMobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileOpen]);

  /* Esc + resize listeners */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsMobileOpen(false);
        setIsProfileOpen(false);
      }
    };
    const onResize = () => {
      if (window.innerWidth >= 768) setIsMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  /* Close profile menu on outside click */
  useEffect(() => {
    if (!isProfileOpen) return;
    const onClick = (e) => {
      if (profileRef.current?.contains(e.target)) return;
      if (mobileProfileRef.current?.contains(e.target)) return;
      setIsProfileOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [isProfileOpen]);

  const handleCategorySelect = (e) => {
    e.stopPropagation();
    const category = e.currentTarget.getAttribute("category");
    dispatch(menuActions.changeActiveCategory({ category }));
    setIsMobileOpen(false);
  };

  const goHome = (e) => {
    e?.stopPropagation?.();
    dispatch(menuActions.changeActiveCategory({ category: "" }));
    navigate("/");
  };

  const goProfile = () => navigate("/profile");

  const handleLogout = () => {
    dispatch(authActions.removeToken());
    dispatch(menuActions.changeActiveCategory(""));
    localStorage.removeItem("giz_superaccess");
    localStorage.removeItem("giz_superrefresh");
    navigate("/login");
    window.location.reload();
  };

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const initials = `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() || "?";

  /* Render the grouped nav: each group gets an uppercase label header. */
  const renderMenuGroups = () =>
    MENU_GROUPS.filter((group) => group.items.length > 0).map((group, groupIdx) => (
      <div key={group.label} className="flex flex-col gap-1">
        <p
          className={`px-3 pb-0 text-[10px] font-semibold uppercase tracking-wider text-gray-400 ${
            groupIdx === 0 ? "pt-1" : "pt-2"
          }`}
        >
          {group.label}
        </p>
        {group.items.map((item) => (
          <MenuItem
            key={item.category}
            category={item.category}
            title={item.title}
            icon={item.icon}
            linkTo={item.linkTo}
            handleClick={handleCategorySelect}
            activeCategory={activeCategory}
            isBigMenu={true}
            badge={item.category === "complaints" ? unansweredComplaints : undefined}
          />
        ))}
      </div>
    ));

  return (
    <>
      {/* ---------- Desktop sidebar ---------- */}
      <aside
        className="w-[280px] hidden md:flex h-full flex-none border border-gray-200 rounded-2xl flex-col bg-white overflow-hidden shadow-sm"
        aria-label="Menuja"
      >
        {/* Brand */}
        <div className="flex items-center h-16 border-b border-gray-100 justify-between gap-2 px-4">
          <button
            type="button"
            onClick={goHome}
            className="flex items-center gap-2.5 min-w-0"
            aria-label="Ballina"
          >
            <img src="/pics/atk.svg" alt="" width={36} className="shrink-0" />
            <img src="/pics/text_logo.png" alt="" className="h-6 max-w-[150px]" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden p-3 flex flex-col gap-1 scrollbar-thin">
          {renderMenuGroups()}
        </nav>

        {/* Profile */}
        <div ref={profileRef} className="relative border-t border-gray-100 p-3">
          <ProfileTrigger
            user={user}
            fullName={fullName}
            initials={initials}
            isOpen={isProfileOpen}
            onToggle={() => setIsProfileOpen((v) => !v)}
          />
          <ProfileMenu
            user={user}
            fullName={fullName}
            initials={initials}
            isOpen={isProfileOpen}
            onClose={() => setIsProfileOpen(false)}
            onProfile={goProfile}
            onLogout={handleLogout}
          />
        </div>
      </aside>

      {/* ---------- Mobile top bar ---------- */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white/90 backdrop-blur border-b border-gray-200 flex items-center justify-between px-3">
        <button
          type="button"
          onClick={() => setIsMobileOpen(true)}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition"
          aria-label="Hap menunë"
        >
          <FiMenu size={22} />
        </button>
        <button
          type="button"
          onClick={goHome}
          className="flex items-center gap-2"
          aria-label="Ballina"
        >
          <img src="/pics/atk.svg" alt="" className="w-8 h-8" />
          <img src="/pics/text_logo.png" alt="" className="h-6" />
        </button>
        <div className="w-10" />
      </header>

      {/* ---------- Mobile backdrop ---------- */}
      <div
        onClick={() => setIsMobileOpen(false)}
        className={`md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />

      {/* ---------- Mobile drawer ---------- */}
      <aside
        className={`md:hidden fixed top-0 left-0 bottom-0 z-50 w-[280px] max-w-[85vw] bg-white border-r border-gray-200 shadow-xl flex flex-col transform transition-transform duration-300 ease-out ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isMobileOpen}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <button
            type="button"
            onClick={() => {
              goHome();
              setIsMobileOpen(false);
            }}
            className="flex items-center gap-2"
          >
            <img src="/pics/atk.svg" alt="" width={36} />
            <img src="/pics/text_logo.png" alt="" className="h-7" />
          </button>
          <button
            type="button"
            onClick={() => setIsMobileOpen(false)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition"
            aria-label="Mbyll menunë"
          >
            <FiX size={22} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 flex flex-col gap-1">
          {renderMenuGroups()}
        </nav>

        <div ref={mobileProfileRef} className="relative border-t border-gray-100 p-3">
          <ProfileTrigger
            user={user}
            fullName={fullName}
            initials={initials}
            isOpen={isProfileOpen}
            onToggle={() => setIsProfileOpen((v) => !v)}
          />
          <ProfileMenu
            user={user}
            fullName={fullName}
            initials={initials}
            isOpen={isProfileOpen}
            onClose={() => setIsProfileOpen(false)}
            onProfile={() => {
              setIsMobileOpen(false);
              goProfile();
            }}
            onLogout={handleLogout}
          />
        </div>
      </aside>
    </>
  );
}
